import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useNotifications, useUnreadNotificationsCount, QUERY_KEYS } from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";
import {
  Bell,
  CheckCheck,
  Heart,
  Wallet,
  Users,
  PartyPopper,
  CreditCard,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  actionUrl?: string;
  createdAt: string;
}

function getNotificationIcon(type: string) {
  switch (type) {
    case "contribution_received":
      return <Heart className="h-4 w-4 text-green-600 dark:text-green-400" />;
    case "wallet_invitation":
      return <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    case "welcome":
    case "onboarding_complete":
      return <PartyPopper className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
    case "payment_plan":
      return <CreditCard className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
    case "wallet_update":
      return <Wallet className="h-4 w-4 text-foreground" />;
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />;
  }
}

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const { t } = useTranslation();

  const { data: notificationsData = [] } = useNotifications({
    refetchInterval: 30000,
  });

  const { data: unreadData } = useUnreadNotificationsCount({
    refetchInterval: 15000,
  });

  const unreadCount = unreadData?.count || 0;

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadNotificationsCount });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadNotificationsCount });
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1"
              data-testid="badge-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 sm:w-96 p-0"
        align="end"
        sideOffset={8}
      >
        <div className="flex items-center justify-between gap-2 p-4 pb-2">
          <h3 className="font-semibold text-sm" data-testid="text-notifications-title">{t('notification-center.heading')}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              data-testid="button-mark-all-read"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              {t('notification-center.mark-all-read')}
            </Button>
          )}
        </div>
        <Separator />
        <ScrollArea className="max-h-[360px]">
          {notificationsData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground" data-testid="status-notifications-empty">
              <Bell className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm" data-testid="text-notifications-empty">{t('notification-center.empty-state')}</p>
              <p className="text-xs mt-1" data-testid="text-notifications-empty-subtitle">{t('notification-center.empty-state-hint')}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notificationsData.map((notification) => (
                <div
                  key={notification.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotificationClick(notification)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleNotificationClick(notification); }}
                  className={`w-full text-left p-3 cursor-pointer transition-colors hover-elevate ${
                    !notification.isRead ? "bg-primary/5" : ""
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5 shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={`text-sm ${!notification.isRead ? "font-semibold" : "font-medium"}`}
                          data-testid={`text-notification-title-${notification.id}`}
                        >
                          {notification.title}
                        </p>
                        {!notification.isRead && (
                          <span className="shrink-0 mt-1.5 h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <p
                        className="text-xs text-muted-foreground mt-0.5 line-clamp-2"
                        data-testid={`text-notification-message-${notification.id}`}
                      >
                        {notification.message}
                      </p>
                      <p
                        className="text-[10px] text-muted-foreground/70 mt-1"
                        data-testid={`text-notification-time-${notification.id}`}
                      >
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
